import AsyncAlpine from "async-alpine";
import Alpine from "alpinejs";

import "../js/lib/global.js";
import "../js/lib/theme-editor.js";
import "../js/lib/video-player.js";

import { cartStore } from "../js/utils/cart-store.js";
import { globalBackdropStore } from "../js/utils/backdrop-store.js";
import { quickViewStore } from "../js/utils/quick-view-store.js";
import { infoDrawerStore } from "../js/utils/info-drawer-store.js";
import sizeGuide from "../js/components/size-guide.js";
import sizeGuideDrawer from "../js/components/size-guide-drawer.js";
import variantPicker from "../js/components/variant-picker.js";
import scrollImageFallback from "../js/components/scroll-image-fallback.js";
import horizontalFadeScroll from "../js/components/horizontal-fade-scroll.js";
import colorSwitcher from "../js/components/color-switcher.js";
import navigationHighlightsHover from "../js/components/navigation-highlights-hover.js";
import "../js/utils/link-prefetcher.js";
import "../js/lib/cookie-banner.js";

window.Alpine = Alpine;

document.addEventListener("alpine:init", () => {
  Alpine.store("cart", cartStore());
  Alpine.store("backdrop", globalBackdropStore());
  Alpine.store("quickview", quickViewStore());
  Alpine.store("infodrawer", infoDrawerStore());

  // Search sort store
  Alpine.store("searchSort", {
    currentSort: "relevance",
    currentSortLabel: "",
    originalOrder: [],
    hasAppliedSort: false,

    sortProducts(sortType) {
      this.currentSort = sortType;
      this.hasAppliedSort = true;

      // Update label based on sort type
      const labels = {
        relevance: "Relevance",
        "price-low": "Price: Low to High",
        "price-high": "Price: High to Low",
        name: "Alphabetical"
      };
      this.currentSortLabel = labels[sortType] || "Relevance";

      // Apply sorting to current results
      this.applySortToResults();
    },

    applySortToResults() {
      const productsList = document.querySelector("#search-products-list");
      if (!productsList) return;

      const products = Array.from(productsList.children);
      if (products.length === 0) return;

      // Store original order if not already stored or if it's different
      if (this.originalOrder.length === 0 || this.originalOrder.length !== products.length) {
        this.originalOrder = products.map((product) => product.cloneNode(true));
      }

      // Sort products based on current sort
      if (this.currentSort === "relevance") {
        // Restore original order
        productsList.innerHTML = "";
        this.originalOrder.forEach((product) => productsList.appendChild(product.cloneNode(true)));
      } else {
        products.sort((a, b) => {
          switch (this.currentSort) {
            case "price-low":
              return this.getProductPrice(a) - this.getProductPrice(b);
            case "price-high":
              return this.getProductPrice(b) - this.getProductPrice(a);
            case "name":
              return this.getProductTitle(a).localeCompare(this.getProductTitle(b));
            default:
              return 0;
          }
        });

        // Re-append sorted products
        productsList.innerHTML = "";
        products.forEach((product) => productsList.appendChild(product));
      }
    },

    storeOriginalOrder() {
      const productsList = document.querySelector("#search-products-list");
      if (productsList) {
        const products = Array.from(productsList.children);
        this.originalOrder = products.map((product) => product.cloneNode(true));
      }
    },

    resetSort() {
      this.currentSort = "relevance";
      this.currentSortLabel = "";
      this.hasAppliedSort = false;
      this.originalOrder = [];
    },

    getProductPrice(productElement) {
      const cardElement = productElement.querySelector("[data-price]") || productElement;
      const price = cardElement.getAttribute("data-price");
      return price ? parseFloat(price) / 100 : 0; // Shopify prices are in cents
    },

    getProductTitle(productElement) {
      const cardElement = productElement.querySelector("[data-title]") || productElement;
      return cardElement.getAttribute("data-title") || "";
    }
  });

  Alpine.data("sizeGuide", sizeGuide);
  Alpine.data("sizeGuideDrawer", sizeGuideDrawer);
  Alpine.data("variantPicker", variantPicker);
  Alpine.data("scrollImageFallback", scrollImageFallback);
  Alpine.data("horizontalFadeScroll", horizontalFadeScroll);
  Alpine.data("colorSwitcher", colorSwitcher);
  Alpine.data("navigationHighlightsHover", navigationHighlightsHover);
});

AsyncAlpine.init(Alpine);

// Theme default
AsyncAlpine.data("search", () => import("../js/components/search.js"));
AsyncAlpine.data("announcementBar", () => import("../js/components/announcement-bar.js"));
AsyncAlpine.data("cart", () => import("../js/components/cart.js"));
AsyncAlpine.data("dynamicHeight", () => import("../js/components/dynamic-height.js"));
AsyncAlpine.data("header", () => import("../js/components/header.js"));
AsyncAlpine.data("headerMenu", () => import("../js/components/header-menu.js"));
AsyncAlpine.data("newSlider", () => import("../js/components/new-slider.js"));
AsyncAlpine.data("imageZoomModal", () => import("../js/components/image-zoom-modal.js"));
AsyncAlpine.data("countryRecommendationModal", () => import("../js/components/country-recommendation.js"));
AsyncAlpine.data("notifyMe", () => import("../js/components/notify-me.js"));

// Components
AsyncAlpine.data("relatedProducts", () => import("../js/components/related-products.js"));
AsyncAlpine.data("overlay", () => import("../js/components/overlay.js"));

AsyncAlpine.data("productDetailsTabs", () => import("../js/components/product-details-tabs.js"));
AsyncAlpine.data("slidingHero", () => import("../js/components/sliding-hero.js"));
AsyncAlpine.data("grid", () => import("../js/components/grid.js"));
AsyncAlpine.data("stickyGridToggle", () => import("../js/components/sticky-grid-toggle.js"));

// Snippets
AsyncAlpine.data("cta", () => import("../js/components/cta.js"));
AsyncAlpine.data("media", () => import("../js/components/media.js"));
AsyncAlpine.data("scrollableNav", () => import("../js/components/scrollable-nav.js"));

// Sections
AsyncAlpine.data("accordionItem", () => import("../js/components/accordion-item.js"));
AsyncAlpine.data("quickView", () => import("../js/components/quick-view.js"));
AsyncAlpine.data("infoView", () => import("../js/components/info-view.js"));
AsyncAlpine.data("countrySelector", () => import("../js/components/country-selector.js"));
AsyncAlpine.data("categoryPromotion", () => import("../js/components/category-promotion.js"));
AsyncAlpine.data("inspirationMenu", () => import("../js/components/inspiration-menu.js"));

// Bundle builder (product.bundle-builder template)
AsyncAlpine.data("bundleIntro", () => import("../js/components/bundle-intro.js").then((module) => module.default));
AsyncAlpine.data("bundleBuilder", () => import("../js/components/bundle-builder.js").then((module) => module.default));
AsyncAlpine.data("bundleSummary", () => import("../js/components/bundle-summary.js").then((module) => module.default));
AsyncAlpine.data("dropdown", () => import("../js/lib/dropdown.js").then((module) => module.default));

AsyncAlpine.start();
Alpine.start();
